/**
 * Wspólny cache zdjęć pojazdów (lista pomiarów <-> szczegóły pomiaru).
 *
 * Po wejściu w szczegóły pojazdu zdjęcie było dociągane po raz drugi, mimo że lista
 * pobrała je chwilę wcześniej. Cache trzyma gotowe base64 (data URL) w pamięci karty,
 * dzięki czemu:
 *  - to samo zdjęcie (ta sama skala) wyświetla się natychmiast, bez żądania do API,
 *  - miniatura z listy służy jako natychmiastowy podkład, dopóki w szczegółach nie
 *    dociągnie się wersja w pełnej rozdzielczości,
 *  - równoległe żądania tego samego zdjęcia są scalane w jedno (deduplikacja).
 *
 * Cache jest świadomie ograniczony (LRU) — lista pomiarów bieżących odświeża się cyklicznie
 * i bez limitu rosłaby w nieskończoność.
 */

// Maksymalna liczba trzymanych zdjęć. Miniatury listy to zwykle kilkadziesiąt kB,
// pełne zdjęcia szczegółów kilkaset kB — 60 wpisów to bezpieczny kompromis.
const MAX_ENTRIES = 60;

// Drugi limit — po rozmiarze. Sama liczba wpisów nie chroni pamięci karty, bo pełne zdjęcie
// w base64 może mieć kilka MB (przeglądanie kolejnych pomiarów szybko by je zsumowało).
const MAX_BYTES = 48 * 1024 * 1024;

// key -> data URL (Map zachowuje kolejność wstawiania, więc realizuje LRU)
const cache = new Map();
// Przybliżony rozmiar zawartości cache (długość stringów base64 ~ liczba bajtów w pamięci)
let totalBytes = 0;
// key -> Promise<data URL> dla żądań w toku (deduplikacja)
const inflight = new Map();

/**
 * Buduje klucz cache. `scope` rozdziela pomiary bieżące od archiwalnych — id pomiaru
 * jest unikalne tylko w obrębie jednej z tych baz.
 */
export function buildImageKey({ scope, vehicleId, locationId, typeId, scaled }) {
  return [
    scope || 'unknown',
    vehicleId,
    locationId,
    typeId,
    scaled ? 'scaled' : 'full',
  ].join('|');
}

/**
 * Zwraca zdjęcie z cache (i odświeża jego pozycję w LRU) albo '' gdy go nie ma.
 */
export function readImage(key) {
  if (!cache.has(key)) {
    return '';
  }

  const dataUrl = cache.get(key);
  // Ponowne wstawienie przenosi wpis na koniec — najdawniej używane wypadają pierwsze.
  cache.delete(key);
  cache.set(key, dataUrl);

  return dataUrl;
}

/**
 * Zapisuje zdjęcie w cache, usuwając najdawniej używane wpisy po przekroczeniu limitu.
 */
export function storeImage(key, dataUrl) {
  if (!dataUrl) {
    return;
  }

  dropImage(key);
  cache.set(key, dataUrl);
  totalBytes += dataUrl.length;

  while (cache.size > MAX_ENTRIES || (totalBytes > MAX_BYTES && cache.size > 1)) {
    dropImage(cache.keys().next().value);
  }
}

/**
 * Usuwa jeden wpis, pilnując licznika rozmiaru.
 */
function dropImage(key) {
  if (!cache.has(key)) {
    return;
  }

  totalBytes -= cache.get(key).length;
  cache.delete(key);
}

/**
 * Zwraca zdjęcie z cache, a gdy go nie ma — pobiera je przez `fetcher` (jedno żądanie
 * na klucz, nawet gdy o to samo zdjęcie pyta kilka komponentów naraz).
 */
export function loadImage(key, fetcher) {
  const cached = readImage(key);
  if (cached) {
    return Promise.resolve(cached);
  }

  const pending = inflight.get(key);
  if (pending) {
    return pending;
  }

  const request = Promise.resolve()
    .then(() => fetcher())
    .then((dataUrl) => {
      storeImage(key, dataUrl);
      return dataUrl || '';
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, request);

  return request;
}

/**
 * Czyści cache (np. przy wylogowaniu — zdjęcia poprzedniego użytkownika nie mogą zostać w pamięci).
 */
export function clearImageCache() {
  cache.clear();
  inflight.clear();
  totalBytes = 0;
}
